import type { CostServiceTrigger } from '../generated/enums.js';
import { CostServiceError, type CostServiceErrorCode } from './errors.js';

/**
 * Cost Services Phase 1: the knob catalog, the Royalty template and the validation that needs no
 * database. One pure module (ALC-4474) so the API, the MCP tools and the screen read the same
 * truth. Spec: docs/features/cost-services/spec-f1-royalty.md, "Scope of Phase 1".
 *
 * Knobs from later phases are deliberately absent: a PATCH naming one is refused with
 * COST_SERVICE_KNOB_UNKNOWN instead of being stored and ignored.
 *
 * No float arithmetic anywhere: rates and tolerances are exact decimal strings and are never
 * rounded. A value with more precision than the column holds is refused.
 */

// --- vocabulary -----------------------------------------------------------------------------

export const COST_SERVICE_KNOB_NAMES = [
  'baseType',
  'rate',
  'vendor',
  'autoMarkVendorBills',
  'currency',
  'costAccount',
  'liabilityAccount',
  'varianceAccount',
  'overrides',
  'activeFrom',
  'activeTo',
  'triggers',
  'tolerance',
  'autoSelectEntriesWithinTolerance',
  'warnOnExpenseWithOpenProvisions',
  'staleAfterDays',
  'fixedRecognitionMonths',
  'chargeFixedOnRenewal',
] as const;

export type CostServiceKnobName = (typeof COST_SERVICE_KNOB_NAMES)[number];

/**
 * `FIXED_PER_MONTH` (D39, "Valor fixo por mês"): the amount is charged once per (Cost Service,
 * contract item, book, competency month) the attached Billing Service recognises revenue for.
 */
export const COST_SERVICE_BASE_TYPES = [
  'PERCENT_OF_REVENUE',
  'FIXED_PER_CONTRACT',
  'FIXED_PER_MONTH',
] as const;
export type CostServiceBaseType = (typeof COST_SERVICE_BASE_TYPES)[number];

/** The base types whose rate is an amount (`1200.00`), not a percentage. */
export const COST_SERVICE_FIXED_BASE_TYPES = ['FIXED_PER_CONTRACT', 'FIXED_PER_MONTH'] as const;
export type CostServiceFixedBaseType = (typeof COST_SERVICE_FIXED_BASE_TYPES)[number];

/**
 * The one "is this a fixed amount?" predicate (API, services, MCP and web): FIXED_PER_CONTRACT
 * (D22) or FIXED_PER_MONTH (D39). A Prisma `in` filter uses `COST_SERVICE_FIXED_BASE_TYPES`.
 */
export function isCostServiceFixedBaseType(
  baseType: string | null | undefined,
): baseType is CostServiceFixedBaseType {
  return (
    typeof baseType === 'string' &&
    (COST_SERVICE_FIXED_BASE_TYPES as readonly string[]).includes(baseType)
  );
}

/**
 * "Valor fixo por contrato" — the one base with a plan to spread (D22 "Distribuir em N meses") and
 * a term to renew (D40 "Cobrar de novo na renovação"). "Valor fixo por mês" (D39) is already one
 * charge per month of revenue, in every term, so the catalog refuses both knobs on it.
 */
export function isCostServiceFixedPerContractBase(
  baseType: string | null | undefined,
): baseType is 'FIXED_PER_CONTRACT' {
  return baseType === 'FIXED_PER_CONTRACT';
}

/** The three revenue seams ADR 0024 hooks into. `ACTIVATION` and the rest are later phases. */
export const COST_SERVICE_TRIGGERS = [
  'SCHEDULE',
  'INVOICE_ISSUED',
  'SETTLEMENT_APPROVED',
] as const satisfies readonly CostServiceTrigger[];

export const COST_SERVICE_KNOB_SOURCES = ['TEMPLATE', 'DEFAULT', 'USER', 'MCP'] as const;
export type CostServiceKnobSource = (typeof COST_SERVICE_KNOB_SOURCES)[number];

/** Who is writing: the screen is USER, an MCP tool call is MCP. */
export type CostServiceWriter = 'USER' | 'MCP';

export const COST_SERVICE_TEMPLATE_NAMES = ['Royalty'] as const;
export type CostServiceTemplateName = (typeof COST_SERVICE_TEMPLATE_NAMES)[number];

/**
 * Lowest value of `staleAfterDays`: the column carries `CHECK (stale_after_days > 0)`, so a zero
 * the catalog let through would reach the caller as an unclassified 500 instead of a stable code.
 * "No limit" is `null`, not zero.
 */
export const COST_SERVICE_STALE_AFTER_DAYS_MIN = 1;

/** Highest value of `staleAfterDays`: ten years is already "never". */
export const COST_SERVICE_STALE_AFTER_DAYS_MAX = 3650;

/**
 * D22 schedule: "Distribuir em N meses" spreads a FIXED_PER_CONTRACT amount over 1..120 months
 * (ten years). `null` is "De uma vez": the whole amount on the contract's first recognised revenue.
 */
export const COST_SERVICE_FIXED_RECOGNITION_MONTHS_MIN = 1;
export const COST_SERVICE_FIXED_RECOGNITION_MONTHS_MAX = 120;

// --- knob values ----------------------------------------------------------------------------

export interface CostServiceKnobValues {
  baseType: CostServiceBaseType;
  /** Canonical text: `40%` for PERCENT_OF_REVENUE, `1200.00` for FIXED_PER_CONTRACT / MONTH. */
  rate: string | null;
  vendor: string | null;
  /**
   * D27: "Marcar todas as contas a pagar desse fornecedor como custo de serviço automaticamente".
   * Intake stamps a new bill of this vendor and entity COST_PROVISION. Needs a vendor.
   */
  autoMarkVendorBills: boolean;
  currency: string;
  costAccount: string | null;
  liabilityAccount: string | null;
  varianceAccount: string | null;
  /** Derived from the attachments; never written through the service PATCH. */
  overrides: unknown[];
  /** `YYYY-MM-DD`. */
  activeFrom: string | null;
  activeTo: string | null;
  triggers: CostServiceTrigger[];
  /** `EXACT`, an amount (`10.50`) or a percentage (`1.5%`). */
  tolerance: string;
  autoSelectEntriesWithinTolerance: boolean;
  warnOnExpenseWithOpenProvisions: boolean;
  staleAfterDays: number | null;
  /**
   * D22 schedule, FIXED_PER_CONTRACT only: the fixed amount spread over this many months (equal
   * parts, the last takes the rounding), from the competency month of the contract's first
   * recognised revenue. `null` = "De uma vez" (all of it on that first revenue).
   */
  fixedRecognitionMonths: number | null;
  /**
   * D40, FIXED_PER_CONTRACT only: "Cobrar o valor fixo de novo a cada renovação". Off (default) =
   * one charge per contract for its whole life (D22); on = one charge per contract TERM, a term
   * starting at each APPROVED RENEWAL amendment's `renewalTermStartDate`.
   */
  chargeFixedOnRenewal: boolean;
}

export type CostServiceKnobSources = Record<CostServiceKnobName, CostServiceKnobSource>;

export interface CostServiceKnobState {
  template: string;
  knobs: CostServiceKnobValues;
  sources: CostServiceKnobSources;
}

/** The refusals this module can produce without a database. */
export type CostServiceKnobIssueCode = Extract<
  CostServiceErrorCode,
  | 'COST_SERVICE_KNOB_UNKNOWN'
  | 'COST_SERVICE_KNOB_VALUE_INVALID'
  | 'COST_SERVICE_TOLERANCE_INVALID'
  | 'COST_SERVICE_VENDOR_REQUIRED'
  | 'COST_SERVICE_RATE_REQUIRED'
  | 'COST_SERVICE_ACTIVE_RANGE_INVALID'
  | 'COST_SERVICE_FIXED_SCHEDULE_NOT_FOR_MONTHLY'
  | 'COST_SERVICE_RENEWAL_CHARGE_NOT_FOR_BASE'
>;

export interface CostServiceKnobIssue {
  code: CostServiceKnobIssueCode;
  knob: string;
}

// --- the catalog ----------------------------------------------------------------------------

export type CostServiceKnobKind =
  | 'enum'
  | 'rate'
  | 'uuid'
  | 'currency'
  | 'date'
  | 'multi'
  | 'list'
  | 'tolerance'
  | 'bool'
  | 'integer';

export type CostServiceKnobGroup = 'DEFINITION' | 'LINK' | 'SETTLEMENT';

export interface CostServiceKnobDefinition {
  readonly name: CostServiceKnobName;
  readonly group: CostServiceKnobGroup;
  readonly kind: CostServiceKnobKind;
  readonly label: { readonly ptBR: string; readonly en: string };
  /** Value when neither the template nor the user said anything. */
  readonly default: CostServiceKnobValues[CostServiceKnobName];
  /** False for knobs the service PATCH never writes (the attachment owns them). */
  readonly writable: boolean;
  /** The allowed values of an enum or multi knob. */
  readonly options?: readonly string[];
  /** The inclusive bounds of an integer knob. */
  readonly min?: number;
  readonly max?: number;
}

const KNOB_DEFINITIONS: CostServiceKnobDefinition[] = [
  {
    name: 'baseType',
    group: 'DEFINITION',
    kind: 'enum',
    label: { ptBR: 'Base do custo', en: 'Cost base' },
    default: 'PERCENT_OF_REVENUE',
    writable: true,
    options: COST_SERVICE_BASE_TYPES,
  },
  {
    name: 'rate',
    group: 'DEFINITION',
    kind: 'rate',
    label: {
      ptBR: 'Taxa do custo (% da receita ou valor fixo)',
      en: 'Cost rate (% of revenue or fixed amount)',
    },
    default: null,
    writable: true,
  },
  {
    name: 'vendor',
    group: 'DEFINITION',
    kind: 'uuid',
    label: { ptBR: 'Fornecedor', en: 'Vendor' },
    default: null,
    writable: true,
  },
  {
    name: 'autoMarkVendorBills',
    group: 'DEFINITION',
    kind: 'bool',
    label: {
      ptBR: 'Marcar todas as contas a pagar desse fornecedor como custo de serviço automaticamente',
      en: "Mark all of this vendor's bills as service cost automatically",
    },
    default: false,
    writable: true,
  },
  {
    name: 'currency',
    group: 'DEFINITION',
    kind: 'currency',
    label: { ptBR: 'Moeda do fornecedor', en: 'Vendor currency' },
    default: 'BRL',
    writable: true,
  },
  {
    name: 'costAccount',
    group: 'DEFINITION',
    kind: 'uuid',
    label: { ptBR: 'Conta de custo', en: 'Cost account' },
    default: null,
    writable: true,
  },
  {
    name: 'liabilityAccount',
    group: 'DEFINITION',
    kind: 'uuid',
    label: {
      ptBR: 'Conta de passivo (provisão a pagar)',
      en: 'Liability account (provision payable)',
    },
    default: null,
    writable: true,
  },
  {
    name: 'varianceAccount',
    group: 'DEFINITION',
    kind: 'uuid',
    label: { ptBR: 'Conta de variação', en: 'Variance account' },
    default: null,
    writable: true,
  },
  {
    name: 'overrides',
    group: 'LINK',
    kind: 'list',
    label: {
      ptBR: 'Taxas específicas por serviço de faturamento',
      en: 'Per billing service rates',
    },
    default: [],
    writable: false,
  },
  {
    name: 'activeFrom',
    group: 'LINK',
    kind: 'date',
    label: { ptBR: 'Ativo a partir de', en: 'Active from' },
    default: null,
    writable: true,
  },
  {
    name: 'activeTo',
    group: 'LINK',
    kind: 'date',
    label: { ptBR: 'Ativo até', en: 'Active until' },
    default: null,
    writable: true,
  },
  {
    name: 'triggers',
    group: 'LINK',
    kind: 'multi',
    label: { ptBR: 'Gatilhos', en: 'Triggers' },
    default: [],
    writable: true,
    options: COST_SERVICE_TRIGGERS,
  },
  {
    name: 'tolerance',
    group: 'SETTLEMENT',
    kind: 'tolerance',
    label: { ptBR: 'Tolerância', en: 'Tolerance' },
    default: 'EXACT',
    writable: true,
  },
  {
    name: 'autoSelectEntriesWithinTolerance',
    group: 'SETTLEMENT',
    kind: 'bool',
    label: { ptBR: 'Selecionar sozinho dentro da tolerância', en: 'Auto-select within tolerance' },
    default: false,
    writable: true,
  },
  {
    name: 'warnOnExpenseWithOpenProvisions',
    group: 'SETTLEMENT',
    kind: 'bool',
    label: {
      ptBR: 'Avisar despesa com provisões em aberto',
      en: 'Warn on expense with open provisions',
    },
    default: true,
    writable: true,
  },
  {
    name: 'staleAfterDays',
    group: 'SETTLEMENT',
    kind: 'integer',
    label: { ptBR: 'Dias até a provisão envelhecer', en: 'Days until a provision goes stale' },
    default: null,
    writable: true,
    min: COST_SERVICE_STALE_AFTER_DAYS_MIN,
    max: COST_SERVICE_STALE_AFTER_DAYS_MAX,
  },
  {
    name: 'fixedRecognitionMonths',
    group: 'DEFINITION',
    kind: 'integer',
    label: { ptBR: 'Reconhecimento do valor fixo', en: 'Fixed amount recognition' },
    default: null,
    writable: true,
    min: COST_SERVICE_FIXED_RECOGNITION_MONTHS_MIN,
    max: COST_SERVICE_FIXED_RECOGNITION_MONTHS_MAX,
  },
  {
    name: 'chargeFixedOnRenewal',
    group: 'DEFINITION',
    kind: 'bool',
    label: {
      ptBR: 'Cobrar o valor fixo de novo a cada renovação do contrato',
      en: 'Charge the fixed amount again on each contract renewal',
    },
    default: false,
    writable: true,
  },
];

export const COST_SERVICE_KNOB_CATALOG: readonly CostServiceKnobDefinition[] =
  Object.freeze(KNOB_DEFINITIONS);

const CATALOG_BY_NAME: ReadonlyMap<string, CostServiceKnobDefinition> = new Map(
  COST_SERVICE_KNOB_CATALOG.map((knob) => [knob.name, knob]),
);

/** The knobs a template names; every other knob falls back to the catalog default. */
const TEMPLATE_PRESETS: Readonly<
  Record<CostServiceTemplateName, Readonly<Partial<CostServiceKnobValues>>>
> = {
  Royalty: {
    baseType: 'PERCENT_OF_REVENUE',
    triggers: ['SCHEDULE', 'INVOICE_ISSUED', 'SETTLEMENT_APPROVED'],
    tolerance: '1%',
  },
};

// --- exact decimals (strings only, no floats) -----------------------------------------------

interface Decimal {
  /** No leading zeros, `0` for zero. */
  int: string;
  /** No trailing zeros. */
  frac: string;
}

function parseUnsignedDecimal(
  raw: string,
  maxFraction: number,
  maxInteger: number,
): Decimal | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!match) return null;
  const int = (match[1] ?? '').replace(/^0+(?=\d)/, '');
  const frac = (match[2] ?? '').replace(/0+$/, '');
  if (int.length > maxInteger || frac.length > maxFraction) return null;
  return { int, frac };
}

function isZero(value: Decimal): boolean {
  return value.int === '0' && value.frac === '';
}

/** `value > limit` for a whole-number limit. */
function exceeds(value: Decimal, limit: number): boolean {
  const limitText = String(limit);
  if (value.int.length !== limitText.length) return value.int.length > limitText.length;
  if (value.int !== limitText) return value.int > limitText;
  return value.frac !== '';
}

function toText(value: Decimal): string {
  return value.frac === '' ? value.int : `${value.int}.${value.frac}`;
}

/** Moves the decimal point `places` to the left (divide by 10^places). */
function shiftLeft(value: Decimal, places: number): Decimal {
  const digits = value.int + value.frac;
  let point = value.int.length - places;
  let padded = digits;
  if (point < 0) {
    padded = '0'.repeat(-point) + digits;
    point = 0;
  }
  const int = padded.slice(0, point).replace(/^0+(?=\d)/, '') || '0';
  const frac = padded.slice(point).replace(/0+$/, '');
  return { int, frac };
}

/** Moves the decimal point `places` to the right (multiply by 10^places). */
function shiftRight(value: Decimal, places: number): Decimal {
  const digits = value.int + value.frac.padEnd(places, '0');
  const point = value.int.length + places;
  const int = digits.slice(0, point).replace(/^0+(?=\d)/, '') || '0';
  const frac = digits.slice(point).replace(/0+$/, '');
  return { int, frac };
}

function normalizeSeparator(raw: string): string {
  return raw.trim().replaceAll(',', '.');
}

// --- rate -----------------------------------------------------------------------------------

/** Fraction digits a percentage may carry: `12.3456%` is `0.123456`, six digits in the column. */
const PERCENT_FRACTION_DIGITS = 4;
/** A fixed amount is money: two fraction digits. */
const AMOUNT_FRACTION_DIGITS = 2;
const AMOUNT_INTEGER_DIGITS = 12;

export type CostServiceRateParse = { ok: true; canonical: string; decimal: string } | { ok: false };

/**
 * Reads a rate written for `baseType`. A percentage is `40%`, `40` or `12,5` (the percent sign is
 * optional: the field already says "% of revenue") and becomes the exact fraction `0.4`; a fixed
 * amount is `1200` or `1200.50` and stays an amount. A percentage written with the sign is refused
 * for a fixed amount, a zero rate is refused, a percentage above 100% is refused, and nothing is
 * rounded. Screen, API and MCP all read a rate through here, so they agree on what is typeable.
 */
export function parseCostServiceRate(baseType: string, input: unknown): CostServiceRateParse {
  return readRate(baseType, input, true);
}

/**
 * `percentSignOptional: false` is the strict reading of a rate ALREADY STORED in the canonical
 * form of some base type: a stored amount (`12.50`) must never be reinterpreted as 12.5% just
 * because the base type moved under it.
 */
function readRate(
  baseType: string,
  input: unknown,
  percentSignOptional: boolean,
): CostServiceRateParse {
  if (typeof input !== 'string') return { ok: false };
  const text = normalizeSeparator(input);
  if (baseType === 'PERCENT_OF_REVENUE') {
    const signed = text.endsWith('%');
    if (!signed && !percentSignOptional) return { ok: false };
    const percent = parseUnsignedDecimal(
      (signed ? text.slice(0, -1) : text).trim(),
      PERCENT_FRACTION_DIGITS,
      3,
    );
    if (!percent || isZero(percent) || exceeds(percent, 100)) return { ok: false };
    return {
      ok: true,
      canonical: `${toText(percent)}%`,
      decimal: toText(shiftLeft(percent, 2)),
    };
  }
  if (isCostServiceFixedBaseType(baseType)) {
    const amount = parseUnsignedDecimal(text, AMOUNT_FRACTION_DIGITS, AMOUNT_INTEGER_DIGITS);
    if (!amount || isZero(amount)) return { ok: false };
    return {
      ok: true,
      canonical: `${amount.int}.${amount.frac.padEnd(AMOUNT_FRACTION_DIGITS, '0')}`,
      decimal: toText(amount),
    };
  }
  return { ok: false };
}

/** Turns the stored decimal back into the form the screen and the MCP read (`0.4` -> `40%`). */
export function formatCostServiceRate(baseType: string, decimal: string | null): string | null {
  if (decimal === null) return null;
  const value = parseUnsignedDecimal(decimal.trim(), 12, 18);
  if (!value) return null;
  if (baseType === 'PERCENT_OF_REVENUE') return `${toText(shiftRight(value, 2))}%`;
  return `${value.int}.${value.frac.padEnd(AMOUNT_FRACTION_DIGITS, '0')}`;
}

// --- tolerance ------------------------------------------------------------------------------

export type CostServiceToleranceReading =
  { kind: 'EXACT' } | { kind: 'PERCENT'; value: string } | { kind: 'AMOUNT'; value: string };

/**
 * Reads a tolerance: `EXACT` (also `exato`), an amount (`10`, `10,5`) or a percentage (`2%`).
 * The value is an exact decimal string and is never rounded; null when it is none of the three.
 */
export function parseCostServiceTolerance(input: unknown): CostServiceToleranceReading | null {
  if (typeof input !== 'string') return null;
  const text = normalizeSeparator(input);
  const lower = text.toLowerCase();
  if (lower === 'exact' || lower === 'exato') return { kind: 'EXACT' };
  if (text.endsWith('%')) {
    const percent = parseUnsignedDecimal(text.slice(0, -1), PERCENT_FRACTION_DIGITS, 3);
    if (!percent || exceeds(percent, 100)) return null;
    return { kind: 'PERCENT', value: toText(percent) };
  }
  const amount = parseUnsignedDecimal(text, AMOUNT_FRACTION_DIGITS, AMOUNT_INTEGER_DIGITS);
  if (!amount) return null;
  return {
    kind: 'AMOUNT',
    value: `${amount.int}.${amount.frac.padEnd(AMOUNT_FRACTION_DIGITS, '0')}`,
  };
}

function canonicalTolerance(reading: CostServiceToleranceReading): string {
  if (reading.kind === 'EXACT') return 'EXACT';
  return reading.kind === 'PERCENT' ? `${reading.value}%` : reading.value;
}

// --- per knob value normalisation -----------------------------------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function isRealDate(text: string): boolean {
  const match = DATE_PATTERN.exec(text);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

type Normalized = { ok: true; value: unknown } | { ok: false; code: CostServiceKnobIssueCode };

const INVALID: Normalized = { ok: false, code: 'COST_SERVICE_KNOB_VALUE_INVALID' };

function normalizeKnobValue(definition: CostServiceKnobDefinition, value: unknown): Normalized {
  switch (definition.kind) {
    case 'enum':
      return typeof value === 'string' && definition.options?.includes(value)
        ? { ok: true, value }
        : INVALID;
    case 'rate': {
      if (value === null) return { ok: true, value: null };
      if (typeof value !== 'string') return INVALID;
      // A signed percentage (`12,5%`) can only be a percentage. A bare number (`12,5`, `1200`) is
      // valid in either base type and stays as typed: which one it is depends on the base type
      // the state ends up with, so the base-type stage in applyCostServiceKnobPatch settles it.
      const typed = normalizeSeparator(value);
      if (typed.endsWith('%')) {
        const signed = parseCostServiceRate('PERCENT_OF_REVENUE', typed);
        return signed.ok ? { ok: true, value: signed.canonical } : INVALID;
      }
      const readable =
        parseCostServiceRate('PERCENT_OF_REVENUE', typed).ok ||
        parseCostServiceRate('FIXED_PER_CONTRACT', typed).ok;
      return readable ? { ok: true, value: typed } : INVALID;
    }
    case 'uuid':
      if (value === null) return { ok: true, value: null };
      return typeof value === 'string' && UUID_PATTERN.test(value)
        ? { ok: true, value: value.toLowerCase() }
        : INVALID;
    case 'currency':
      return typeof value === 'string' && /^[A-Za-z]{3}$/.test(value)
        ? { ok: true, value: value.toUpperCase() }
        : INVALID;
    case 'date':
      if (value === null) return { ok: true, value: null };
      return typeof value === 'string' && isRealDate(value) ? { ok: true, value } : INVALID;
    case 'bool':
      return typeof value === 'boolean' ? { ok: true, value } : INVALID;
    case 'integer':
      if (value === null) return { ok: true, value: null };
      return typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= (definition.min ?? Number.MIN_SAFE_INTEGER) &&
        value <= (definition.max ?? Number.MAX_SAFE_INTEGER)
        ? { ok: true, value }
        : INVALID;
    case 'multi': {
      if (!Array.isArray(value)) return INVALID;
      const options = definition.options ?? [];
      const seen = new Set<string>();
      for (const item of value) {
        if (typeof item !== 'string' || !options.includes(item) || seen.has(item)) return INVALID;
        seen.add(item);
      }
      // Catalog order, so the same set always reads the same.
      return { ok: true, value: options.filter((option) => seen.has(option)) };
    }
    case 'list':
      return Array.isArray(value) ? { ok: true, value: [...value] } : INVALID;
    case 'tolerance': {
      const reading = parseCostServiceTolerance(value);
      return reading
        ? { ok: true, value: canonicalTolerance(reading) }
        : { ok: false, code: 'COST_SERVICE_TOLERANCE_INVALID' };
    }
  }
}

/**
 * Refusals for one knob and one value, without applying anything. An unknown knob is
 * COST_SERVICE_KNOB_UNKNOWN, a bad tolerance is COST_SERVICE_TOLERANCE_INVALID, any other bad
 * value is COST_SERVICE_KNOB_VALUE_INVALID. Empty array when the value is acceptable.
 */
export function costServiceKnobIssues(knob: string, value: unknown): CostServiceKnobIssue[] {
  const definition = CATALOG_BY_NAME.get(knob);
  if (!definition) return [{ code: 'COST_SERVICE_KNOB_UNKNOWN', knob }];
  const result = normalizeKnobValue(definition, value);
  return result.ok ? [] : [{ code: result.code, knob }];
}

// --- template state -------------------------------------------------------------------------

function cloneValue<T>(value: T): T {
  return Array.isArray(value) ? ([...value] as unknown as T) : value;
}

function cloneKnobs(knobs: CostServiceKnobValues): CostServiceKnobValues {
  return {
    ...knobs,
    overrides: [...knobs.overrides],
    triggers: [...knobs.triggers],
  };
}

function isTemplateName(name: string): name is CostServiceTemplateName {
  return (COST_SERVICE_TEMPLATE_NAMES as readonly string[]).includes(name);
}

/**
 * The knob state a new Cost Service starts with: the template's preset where it names a knob
 * (source TEMPLATE) and the catalog default everywhere else (source DEFAULT). Throws
 * COST_SERVICE_TEMPLATE_UNKNOWN for a template that does not ship.
 */
export function buildCostServiceTemplateState(templateName: string): CostServiceKnobState {
  if (!isTemplateName(templateName)) {
    throw new CostServiceError('COST_SERVICE_TEMPLATE_UNKNOWN', { template: templateName });
  }
  const preset = TEMPLATE_PRESETS[templateName];
  const knobs: Record<string, unknown> = {};
  const sources: Record<string, CostServiceKnobSource> = {};
  for (const definition of COST_SERVICE_KNOB_CATALOG) {
    const named = definition.name in preset;
    knobs[definition.name] = cloneValue(named ? preset[definition.name] : definition.default);
    sources[definition.name] = named ? 'TEMPLATE' : 'DEFAULT';
  }
  return {
    template: templateName,
    knobs: knobs as unknown as CostServiceKnobValues,
    sources: sources as CostServiceKnobSources,
  };
}

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export type CostServicePatchResult =
  { ok: true; state: CostServiceKnobState } | { ok: false; issues: CostServiceKnobIssue[] };

/**
 * Applies a PATCH to a knob state without mutating it. Atomic: one unknown or unreadable knob
 * refuses the whole PATCH and every issue is reported. A written knob takes the caller as its
 * source (USER from the screen, MCP from a tool); a knob written back to what the template or
 * the catalog holds returns to TEMPLATE or DEFAULT. The rate is checked against the base type
 * the state will have once the PATCH lands, so a rate in the wrong form is refused whether the
 * rate or the base type moved.
 */
export function applyCostServiceKnobPatch(
  state: CostServiceKnobState,
  patch: Readonly<Record<string, unknown>>,
  writer: CostServiceWriter,
): CostServicePatchResult {
  const issues: CostServiceKnobIssue[] = [];
  const next = cloneKnobs(state.knobs) as unknown as Record<string, unknown>;
  const written: CostServiceKnobName[] = [];

  for (const [knob, value] of Object.entries(patch)) {
    const definition = CATALOG_BY_NAME.get(knob);
    if (!definition) {
      issues.push({ code: 'COST_SERVICE_KNOB_UNKNOWN', knob });
      continue;
    }
    if (!definition.writable) {
      issues.push({ code: 'COST_SERVICE_KNOB_VALUE_INVALID', knob });
      continue;
    }
    const result = normalizeKnobValue(definition, value);
    if (!result.ok) {
      issues.push({ code: result.code, knob });
      continue;
    }
    next[knob] = result.value;
    written.push(definition.name);
  }

  const values = next as unknown as CostServiceKnobValues;
  const touchesRate = written.includes('rate') || written.includes('baseType');
  if (issues.length === 0 && touchesRate && values.rate !== null) {
    const rateWritten = written.includes('rate');
    // A rate typed in this PATCH is read for the base type it will have (and canonicalized). A
    // stored rate meeting a NEW base type must already be in that base type's own form.
    const reading = readRate(values.baseType, values.rate, rateWritten);
    if (!reading.ok) {
      issues.push({ code: 'COST_SERVICE_KNOB_VALUE_INVALID', knob: 'rate' });
    } else if (rateWritten) {
      next.rate = reading.canonical;
    }
  }
  // D27: the auto-mark flag names the vendor's bills, so it needs a vendor. Clearing the vendor
  // while the flag is on is refused too: turn the flag off in the same PATCH.
  if (
    issues.length === 0 &&
    values.autoMarkVendorBills === true &&
    values.vendor === null &&
    (written.includes('autoMarkVendorBills') || written.includes('vendor'))
  ) {
    issues.push({ code: 'COST_SERVICE_VENDOR_REQUIRED', knob: 'autoMarkVendorBills' });
  }
  // D22 schedule: spreading over months only means something for a fixed amount. Checked whether
  // the months or the base type moved, so a switch to a percentage cannot keep a stale schedule.
  const touchesSchedule =
    written.includes('fixedRecognitionMonths') || written.includes('baseType');
  // D39: "Valor fixo por mês" is already one charge per month; it has no plan to spread, so it
  // gets its own code (the screen says why instead of "valor inválido").
  if (
    issues.length === 0 &&
    touchesSchedule &&
    values.fixedRecognitionMonths !== null &&
    !isCostServiceFixedPerContractBase(values.baseType)
  ) {
    issues.push({
      code:
        values.baseType === 'FIXED_PER_MONTH'
          ? 'COST_SERVICE_FIXED_SCHEDULE_NOT_FOR_MONTHLY'
          : 'COST_SERVICE_KNOB_VALUE_INVALID',
      knob: 'fixedRecognitionMonths',
    });
  }
  // D40: the renewal flag only means something for a charge per contract. Refused (not ignored)
  // when it is on for another base type, whether the flag or the base type moved; its own code
  // (r4 #11), so the screen says why instead of "valor inválido".
  const touchesRenewal = written.includes('chargeFixedOnRenewal') || written.includes('baseType');
  if (
    issues.length === 0 &&
    touchesRenewal &&
    values.chargeFixedOnRenewal &&
    !isCostServiceFixedPerContractBase(values.baseType)
  ) {
    issues.push({ code: 'COST_SERVICE_RENEWAL_CHARGE_NOT_FOR_BASE', knob: 'chargeFixedOnRenewal' });
  }
  if (issues.length > 0) return { ok: false, issues };

  const base = buildCostServiceTemplateState(state.template);
  const sources: CostServiceKnobSources = { ...state.sources };
  for (const knob of written) {
    sources[knob] = sameValue(values[knob], base.knobs[knob]) ? base.sources[knob] : writer;
  }
  return { ok: true, state: { template: state.template, knobs: values, sources } };
}

export interface CostServiceTemplateStatus {
  name: string;
  modified: boolean;
  /**
   * Knobs the template pre-fills whose source is now USER or MCP, in catalog order. Knobs the
   * template leaves blank (vendor, rate, accounts, ...) never appear here: filling a required
   * input is not departing from the template (D11).
   */
  modifiedKnobs: CostServiceKnobName[];
}

/**
 * D11, the ONE rule for "modified": a knob counts as modified only when the template PRE-FILLS it
 * and a person (USER) or an agent (MCP) wrote it since. A knob the template leaves blank (vendor,
 * rate, accounts) is never modified by being filled in. The header's "modified" badge and each
 * field's own tag both ask this function, so they cannot disagree.
 */
export function isCostServiceKnobModified(
  templateName: string,
  knob: CostServiceKnobName,
  source: CostServiceKnobSource | undefined,
): boolean {
  if (!isTemplateName(templateName)) return false;
  return (
    Object.hasOwn(TEMPLATE_PRESETS[templateName], knob) && (source === 'USER' || source === 'MCP')
  );
}

/**
 * "Modified" means the owner changed a knob the template PRE-FILLS (D11). A Royalty service whose
 * vendor and rate were typed in is still an untouched Royalty; one whose tolerance moved from 1%
 * to 2% is modified, and the status names `tolerance`.
 */
export function costServiceTemplateStatus(state: CostServiceKnobState): CostServiceTemplateStatus {
  const modifiedKnobs = COST_SERVICE_KNOB_NAMES.filter((knob) =>
    isCostServiceKnobModified(state.template, knob, state.sources[knob]),
  );
  return { name: state.template, modified: modifiedKnobs.length > 0, modifiedKnobs };
}

/**
 * The cross-field refusals that need no database: a vendor, a rate, at least one trigger, and an
 * active window that does not end before it starts. The AP-control check needs the chart of
 * accounts and lives in the API. Empty array when the state can be activated as far as this
 * module can tell.
 *
 * An empty `triggers` list is fine on a DRAFT, but an ACTIVE service with no trigger never
 * resolves for any revenue seam, so its costs would be dropped without a word.
 */
export function validateCostServiceForActivation(
  state: CostServiceKnobState,
): CostServiceKnobIssue[] {
  const issues: CostServiceKnobIssue[] = [];
  const { vendor, rate, triggers, activeFrom, activeTo } = state.knobs;
  if (vendor === null) issues.push({ code: 'COST_SERVICE_VENDOR_REQUIRED', knob: 'vendor' });
  if (rate === null) issues.push({ code: 'COST_SERVICE_RATE_REQUIRED', knob: 'rate' });
  if (triggers.length === 0) {
    issues.push({ code: 'COST_SERVICE_KNOB_VALUE_INVALID', knob: 'triggers' });
  }
  if (activeFrom !== null && activeTo !== null && activeTo < activeFrom) {
    issues.push({ code: 'COST_SERVICE_ACTIVE_RANGE_INVALID', knob: 'activeTo' });
  }
  return issues;
}
